import { useState, useEffect } from "react";
import { Breadcrumbs, CircularProgress, Button } from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CustomTable from "../../components/ui/CustomTable/customTable";
import CustomModal from "../../components/ui/CustomModal/customModal";
import Snackbar from "../../components/ui/CustomSnackbar/customSnackbar";
import ApiService from "../../components/apiService/ApiService";
import pageStyles from "../../components/ui/CommonStyles.module.css";
import styles from "./kycRecords.module.css";

const PAGE_SIZE = 14;

function KycRecords() {

  //States
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [response, setResponse] = useState({});
  const [confirmData, setConfirmData] = useState(null); // Structure: {row, isPassed} || null
  const [marking, setMarking] = useState(false);


  //Handlers
  const handleFeedback = (message, success) => {
    setResponse({ message, success });
    setShowSnackbar(true);
  };

  const fetchData = (pageIndex = 0) => {
    setShowLoader(true);
    ApiService.get(
      `/api/v1/dashboard/kyc/inconclusive-results?page=${
        pageIndex + 1
      }&page_size=${PAGE_SIZE}`
    )
      .then((res) => {
        if (res?.data?.success) {
          setRecords(res?.data?.data?.results || []);
          setRowCount(res?.data?.data?.count || 0);
        } else {
          throw new Error(res?.data?.message);
        }
      })
      .catch((err) => {
        handleFeedback(
          err?.response?.data?.message || "Failed to fetch KYC records",
          false
        );
      })
      .finally(()=> setShowLoader(false));
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleMarkResult = () => {
    if (!confirmData) return;
    setMarking(true);
    ApiService.post(`/api/v1/dashboard/kyc/mark-result`, {
      session_id: confirmData.record.session_id,
      is_passed: confirmData.isPassed,
    })
      .then((res) => {
        if (res?.data?.success) {
          handleFeedback(
            `KYC record ${
              confirmData.isPassed ? "accepted" : "rejected"
            } successfully`,
            true
          );
          setConfirmData(null);
          setPage(0);
          fetchData(0);
        } else {
          throw new Error(res?.data?.message)
        }
      })
      .catch((err) => {
        handleFeedback(
          err?.response?.data?.message || "Failed to mark result",
          false
        );
        setConfirmData(null);
      })
      .finally(()=>setMarking(false));
  };

  //Effects
  useEffect(() => {
    fetchData();
  }, []);

  //Columns
  const columns = [
    { field: "session_id", headerName: "Session ID", minWidth: 230, flex: 1.5 },
    {
      field: "organization_id",
      headerName: "Organization ID",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "organization_name",
      headerName: "Organization Name",
      minWidth: 190,
      flex: 1.2,
    },
    {
      field: "result",
      headerName: "Result",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "authentix_console_url",
      headerName: "Authentix Console",
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: (params) =>
        params.row.authentix_console_url ? (
          <a
            href={params.row.authentix_console_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.console_link}
          >
            Open Console
            <OpenInNewRoundedIcon style={{ fontSize: 16 }} />
          </a>
        ) : (
          "-"
        ),
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 230,
      flex: 1.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className={styles.action_buttons}>
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleOutlineRoundedIcon />}
            onClick={() =>
              setConfirmData({ record: params.row, isPassed: true })
            }
            sx={{
              bgcolor: "#34cb65",
              textTransform: "none",
              ":hover": { bgcolor: "#2AA251", color: "white" },
            }}
          >
            Accept
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<CancelOutlinedIcon />}
            onClick={() =>
              setConfirmData({ record: params.row, isPassed: false })
            }
            sx={{ textTransform: "none" }}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={`${pageStyles.page_container} body_gap`}>
      <Breadcrumbs className={pageStyles.breadcrumbs} aria-label="breadcrumb">
        <p>KYC Records ({rowCount})</p>
      </Breadcrumbs>

      {showLoader ? (
        <div className={pageStyles.loader_wrapper}>
          <CircularProgress color="success" size={60} />
        </div>
      ) : (
        <CustomTable
          noRowsOverlayText="Inconclusive KYC Records"
          columns={columns}
          rows={records}
          rowId={(row) => row.session_id}
          pageSize={PAGE_SIZE}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={handlePageChange}
          exportable={false}
          disableCheckboxSelection={true}
        />
      )}

      {confirmData && (
        <CustomModal
          visible={!!confirmData}
          heading={confirmData.isPassed ? "Accept KYC Record" : "Reject KYC Record"}
          closeModal={() => {
            if (!marking) setConfirmData(null);
          }}
          bodyStyles={{
            padding: "1.5em",
            width: "440px",
            height: "auto",
          }}
          body={
            <div>
              <p className={styles.confirm_text}>
                Are you sure you want to mark KYC record for{" "}
                <strong>{confirmData.record.organization_name}</strong>{" "}
                (Org ID: <strong>{confirmData.record.organization_id}</strong>) as{" "}
                <strong>{confirmData.isPassed ? "Accepted" : "Rejected"}</strong>?
              </p>
              
              <div className={styles.confirm_buttons}>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={marking}
                  onClick={() => setConfirmData(null)}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={marking}
                  onClick={handleMarkResult}
                  color={confirmData.isPassed ? "success" : "error"}
                  sx={{
                    textTransform: "none",
                    ...(confirmData.isPassed && {
                      bgcolor: "#34cb65",
                      ":hover": { bgcolor: "#2AA251", color: "white" },
                    }),
                  }}
                >
                  {marking
                    ? "Please wait..."
                    : confirmData.isPassed
                    ? "Accept"
                    : "Reject"}
                </Button>
              </div>
            </div>
          }
        />
      )}

      {showSnackbar && (
        <Snackbar
          message={response.message}
          type={response.success ? "success" : "error"}
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
}

export default KycRecords;
